import axios from "axios";
import {
    Users,
    Activity,
    HelpCircle,
    TrendingUp,
    TrendingDown,
    MessageSquare,
    FileText,
    ArrowUpRight,
    ChevronDown,
    ChevronUp
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LabelList,
} from "recharts";

import EntrepreneurDashboard from "./EntrepreneurDashboard";
import InvestorDashboard from "../siteComponents/InvestorDashboard";

import { notifyUser } from "../utils/notifyUser";

const Dashboard = () => {
    const [user, setUser] = useState(null);
    const [userCount, setUserCount] = useState(0);
    const [activeUserStats, setActiveUserStats] = useState({
        total: 0,
        investor: 0,
        entrepreneur: 0,
        growthPercentage: 0
    });
    const [activeSessions, setActiveSessions] = useState(0);
    const [unrespondedQueries, setUnrespondedQueries] = useState(0);
    const [applicationsStats, setApplicationsStats] = useState({
        entrepreneur: { pending: 0, approved: 0, rejected: 0, onhold: 0 },
        investor: { pending: 0, approved: 0, rejected: 0, onhold: 0 }
    });
    const [appViewRole, setAppViewRole] = useState("entrepreneur");
    const [campaignsStats, setCampaignsStats] = useState({
        pending: 0,
        approved: 0,
        rejected: 0,
        onhold: 0,
        withdrawn: 0,
        cancelled: 0,
    });
    const [investorCount, setInvestorCount] = useState(0);
    const [entrepreneurCount, setEntrepreneurCount] = useState(0);
    const [unreadMessages, setUnreadMessages] = useState([]);
    const [isCommsExpanded, setIsCommsExpanded] = useState(false);

    const navigate = useNavigate();
    const AQI_PRIMARY = "var(--navy)";

    // Enterprise refined colors
    const COLORS = {
        pending: "#F59E0B",   // Amber
        approved: "#10B981",  // Emerald
        rejected: "#EF4444",  // Rose
        onhold: "#6366F1",    // Indigo
        withdrawn: "#8B5CF6", // Violet
        cancelled: "#94A3B8", // Slate 400
        primary: "var(--navy)",   // Public-site navy
        secondary: "#64748B"  // Slate 500
    };

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/user`, {
            method: "GET",
            credentials: "include",
        })
            .then((res) => res.json())
            .then((data) => setUser(data))
            .catch(() => {
                setUser(null);
            });
    }, []);

    // Fetch all dashboard data only for admin users
    useEffect(() => {
        if (!user || user.role !== "admin") return;

        const fetchDashboardData = async () => {
            try {
                const [users, sessions, queries, appStats, campaignsData, roleData, messages] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/count`, { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/active/count`, { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/faq/unresponded-queries/count`, { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/applications-stats`, { withCredentials: true }).catch(() => ({ data: {} })),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/campaigns-stats`, { withCredentials: true }).catch(() => ({ data: {} })),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/role-counts`, { withCredentials: true }).catch(() => ({ data: { total: 0, investor: 0, entrepreneur: 0, growthPercentage: 0 } })),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/messages/unread`, { withCredentials: true }).catch(() => ({ data: [] })),
                ]);

                setUserCount(users.data.count || 0);
                setActiveSessions(sessions.data.count || 0);
                setUnrespondedQueries(queries.data.count || 0);

                const appData = appStats.data || {};
                if (appData.entrepreneur || appData.investor) {
                    setApplicationsStats({
                        entrepreneur: appData.entrepreneur || { pending: 0, approved: 0, rejected: 0, onhold: 0 },
                        investor: appData.investor || { pending: 0, approved: 0, rejected: 0, onhold: 0 }
                    });
                } else {
                    setApplicationsStats({
                        entrepreneur: { pending: appData.pending || 0, approved: appData.approved || 0, rejected: appData.rejected || 0, onhold: appData.onhold || 0 },
                        investor: { pending: 0, approved: 0, rejected: 0, onhold: 0 }
                    });
                }

                setCampaignsStats(campaignsData.data || {});

                // Set active user stats with breakdown
                const roleStats = roleData.data || {};
                setActiveUserStats({
                    total: roleStats.total || 0,
                    investor: roleStats.investor || 0,
                    entrepreneur: roleStats.entrepreneur || 0,
                    growthPercentage: roleStats.growthPercentage || 0
                });
                setInvestorCount(roleStats.investor || 0);
                setEntrepreneurCount(roleStats.entrepreneur || 0);
                setUnreadMessages(messages.data || []);
            } catch (error) {
                notifyUser(null, "error", "Failed to load dashboard statistics", "toast", null, null, null, null, null, false, false, "top-center");
            }
        };

        fetchDashboardData();
    }, [user]);

    // Check OAuth secret reminder status and show toast/send email as needed (admin only)
    useEffect(() => {
        if (!user || user.role !== "admin") return;

        const checkOauthSecret = async () => {
            try {
                const resp = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/configs/oauth-secret-status`, { withCredentials: true });
                const data = resp.data || {};
                const checks = ["GOOGLE_CLIENT_SECRET", "MICROSOFT_CLIENT_SECRET"];
                checks.forEach((key) => {
                    const s = data[key];
                    if (s && s.shouldShowToast) {
                        const label = key.includes('MICROSOFT') ? 'Microsoft' : 'Google';
                        const daysLeft = typeof s.daysLeft === 'number' ? s.daysLeft : null;
                        const message = daysLeft === 0
                            ? `${label} OAuth client secret expires today.`
                            : daysLeft > 0
                                ? `${label} OAuth client secret expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.`
                                : `${label} OAuth client secret expiry passed ${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? '' : 's'} ago.`;

                        notifyUser(null, "warning", message, "toast", null, null, null, null, null, false, false, "top-center");
                    }
                    if (s && s.shouldSendEmail) {
                        const label = key.includes('MICROSOFT') ? 'Microsoft' : 'Google';
                        notifyUser(null, "info", `Reminder email queued to OAuth notification address for ${label}.`, "toast", null, null, null, null, null, false, false, "top-center");
                    }
                });
            } catch (err) {
                // Fail silently — don't block dashboard
            }
        };

        checkOauthSecret();
    }, [user]);

    // Role-based switch
    if (user?.role === "investor") return <InvestorDashboard user={user} />;
    if (user?.role === "entrepreneur") return <EntrepreneurDashboard user={user} />;

    const totalApplications =
        (applicationsStats.entrepreneur.pending + applicationsStats.entrepreneur.approved + applicationsStats.entrepreneur.rejected + applicationsStats.entrepreneur.onhold) +
        (applicationsStats.investor.pending + applicationsStats.investor.approved + applicationsStats.investor.rejected + applicationsStats.investor.onhold);

    const approvedApplications = applicationsStats.entrepreneur.approved + applicationsStats.investor.approved;

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white border border-slate-200 p-3 rounded-lg shadow-lg">
                    <p className="font-semibold text-slate-800 mb-1">{label}</p>
                    {payload.map((pld, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-slate-600">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: pld.fill }} />
                            <span>Count: <strong className="text-slate-900">{pld.value}</strong></span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    // Admin Dashboard
    return (
        <div className="min-h-screen bg-[#F5F7FA] p-4 sm:p-6 lg:p-8 font-sans text-slate-900">
            <div className="max-w-[1400px] mx-auto space-y-6">

                {/* Header (Enterprise standard) */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Overview</h1>
                        <p className="text-sm text-slate-500 mt-1">Platform metrics and activity</p>
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="text-right">
                            <p className="text-sm font-semibold text-slate-900">{user?.first_name} {user?.last_name}</p>
                            <p className="text-xs text-slate-500">{user?.email}</p>
                        </div>
                    </div>
                </div>

                {/* KPI Cards Strip */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {/* KPI 1 - Total Active Users */}
                    <div
                        className="bg-white rounded-xl border border-slate-200/80 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.02)] hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate("/Users")}
                    >
                        <div className="flex justify-between items-start mb-4">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Active Users</p>
                            <div className="p-2 bg-slate-50 rounded-lg border border-slate-100">
                                <Users className="w-4 h-4 text-slate-600" />
                            </div>
                        </div>
                        <div className="flex items-baseline gap-3 mb-2">
                            <h3 className="text-3xl font-bold text-slate-900">{activeUserStats.total.toLocaleString()}</h3>
                            {activeUserStats.growthPercentage !== 0 && (
                                <span
                                    className={`flex items-center text-xs font-semibold px-1.5 py-0.5 rounded-md ${activeUserStats.growthPercentage > 0
                                        ? 'text-emerald-700 bg-emerald-50'
                                        : 'text-rose-700 bg-rose-50'
                                        }`}
                                    title="Growth compared to 30 days ago"
                                >
                                    <TrendingUp className="w-3 h-3 mr-1" />
                                    {activeUserStats.growthPercentage > 0 ? '+' : ''}{activeUserStats.growthPercentage}%
                                </span>
                            )}
                        </div>
                        <div className="space-y-1">
                            <p className="text-xs text-slate-500">
                                {activeUserStats.investor} Investors • {activeUserStats.entrepreneur} Entrepreneurs
                            </p>
                            {activeUserStats.growthPercentage !== 0 && (
                                <p className="text-xs text-slate-400 italic">
                                    {activeUserStats.growthPercentage > 0 ? 'Growth' : 'Change'} vs last 30 days
                                </p>
                            )}
                        </div>
                    </div>

                    {/* KPI 2 - Applications */}
                    <div
                        className="bg-white rounded-xl border border-slate-200/80 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.02)] hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate("/applications")}
                    >
                        <div className="flex justify-between items-start mb-4">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Applications</p>
                            <div className="p-2 bg-indigo-50 rounded-lg border border-indigo-100">
                                <FileText className="w-4 h-4 text-indigo-600" />
                            </div>
                        </div>
                        <div className="flex items-baseline gap-3 mb-4">
                            <h3 className="text-3xl font-bold text-slate-900">
                                {totalApplications.toLocaleString()}
                            </h3>
                            <span className="text-xs font-medium px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md">
                                {approvedApplications} Approved
                            </span>
                        </div>
                        <div className="space-y-2 pt-4 border-t border-slate-100">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-500 font-medium">Entrepreneurs</span>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-slate-900 font-semibold">
                                        {(applicationsStats.entrepreneur.pending + applicationsStats.entrepreneur.approved + applicationsStats.entrepreneur.rejected + applicationsStats.entrepreneur.onhold).toLocaleString()}
                                    </span>
                                    <span className="text-slate-400 px-0.5">|</span>
                                    <span className="text-emerald-600 font-medium bg-emerald-50 px-1.5 py-0.5 rounded">
                                        {applicationsStats.entrepreneur.approved} approved
                                    </span>
                                </div>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-500 font-medium">Investors</span>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-slate-900 font-semibold">
                                        {(applicationsStats.investor.pending + applicationsStats.investor.approved + applicationsStats.investor.rejected + applicationsStats.investor.onhold).toLocaleString()}
                                    </span>
                                    <span className="text-slate-400 px-0.5">|</span>
                                    <span className="text-emerald-600 font-medium bg-emerald-50 px-1.5 py-0.5 rounded">
                                        {applicationsStats.investor.approved} approved
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* KPI 3 - Active Sessions */}
                    <div
                        className="bg-white rounded-xl border border-slate-200/80 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.02)] hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate("/User-sessions")}
                    >
                        <div className="flex justify-between items-start mb-4">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Sessions</p>
                            <div className="p-2 bg-orange-50 rounded-lg border border-orange-100">
                                <Activity className="w-4 h-4 text-orange-500" />
                            </div>
                        </div>
                        <div className="flex items-baseline gap-3 mb-2">
                            <h3 className="text-3xl font-bold text-slate-900">{activeSessions.toLocaleString()}</h3>
                            <span
                                className="flex items-center text-xs font-semibold px-1.5 py-0.5 rounded-md text-emerald-700 bg-emerald-50"
                                title="Growth compared to 30 days ago"
                            >
                                <TrendingUp className="w-3 h-3 mr-1" />
                                +12%
                            </span>
                        </div>
                        <div className="space-y-1">
                            <p className="text-xs text-slate-500">Currently online</p>
                            <p className="text-xs text-slate-400 italic">Growth vs last 30 days</p>
                        </div>
                    </div>

                    {/* KPI 4 - Pending Queries */}
                    <div
                        className="bg-white rounded-xl border border-slate-200/80 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.02)] hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate("/support")}
                    >
                        <div className="flex justify-between items-start mb-4">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending Queries</p>
                            <div className="p-2 bg-rose-50 rounded-lg border border-rose-100">
                                <HelpCircle className="w-4 h-4 text-rose-600" />
                            </div>
                        </div>
                        <div className="flex items-baseline gap-3 mb-2">
                            <h3 className="text-3xl font-bold text-slate-900">{unrespondedQueries.toLocaleString()}</h3>
                            <span
                                className="flex items-center text-xs font-semibold px-1.5 py-0.5 rounded-md text-rose-700 bg-rose-50"
                                title="Change compared to 30 days ago"
                            >
                                <TrendingDown className="w-3 h-3 mr-1" />
                                -18%
                            </span>
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <p className="text-xs text-slate-500">Awaiting response</p>
                                {unrespondedQueries > 0 && (
                                    <span className="text-xs font-medium text-rose-600 flex items-center bg-rose-50 px-1.5 py-0.5 rounded">
                                        <ArrowUpRight className="w-3 h-3 mr-0.5" /> Action needed
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-slate-400 italic">Change vs last 30 days</p>
                        </div>
                    </div>
                </div>

                {/* Secondary Data Row - Charts (Now below KPI cards) */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* App Status Chart */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)] p-6">
                        <div className="mb-6 flex justify-between items-start">
                            <div>
                                <h3 className="text-sm font-bold text-slate-900">Application Status</h3>
                                <p className="text-xs text-slate-500 mt-0.5">Current status of all applications</p>
                            </div>
                            <div className="bg-slate-100 p-1 rounded-lg inline-flex items-center">
                                <button
                                    className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${appViewRole === 'entrepreneur' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                    onClick={() => setAppViewRole('entrepreneur')}
                                >Entrepreneurs</button>
                                <button
                                    className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${appViewRole === 'investor' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                    onClick={() => setAppViewRole('investor')}
                                >Investors</button>
                            </div>
                        </div>
                        <div className="h-[260px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={[
                                        { name: "Pending", value: applicationsStats[appViewRole].pending },
                                        { name: "Approved", value: applicationsStats[appViewRole].approved },
                                        { name: "On Hold", value: applicationsStats[appViewRole].onhold },
                                        { name: "Rejected", value: applicationsStats[appViewRole].rejected },
                                    ]}
                                    margin={{ top: 20, right: 10, left: 10, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 12 }} dy={10} />
                                    <YAxis hide={true} />
                                    <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F5F7FA' }} />
                                    <Bar
                                        dataKey="value"
                                        fill={AQI_PRIMARY}
                                        radius={[4, 4, 0, 0]}
                                        maxBarSize={40}
                                    >
                                        <LabelList dataKey="value" position="top" fill="var(--navy)" fontSize={12} fontWeight={600} offset={10} formatter={(v) => v === 0 ? "0" : v} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Campaigns Status Chart */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)] p-6">
                        <div className="mb-6 flex justify-between items-start">
                            <div>
                                <h3 className="text-sm font-bold text-slate-900">Campaign Overview</h3>
                                <p className="text-xs text-slate-500 mt-0.5">Comprehensive tracking of active statuses</p>
                            </div>
                            <button
                                onClick={() => navigate("/campaigns")}
                                className="flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 transition-colors px-2.5 py-1.5 rounded-md"
                            >
                                View All <ArrowUpRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="h-[260px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={[
                                        { name: "Pending", value: campaignsStats.pending },
                                        { name: "Approved", value: campaignsStats.approved },
                                        { name: "Rejected", value: campaignsStats.rejected },
                                        { name: "On Hold", value: campaignsStats.onhold },
                                        { name: "Withdrawn", value: campaignsStats.withdrawn },
                                        { name: "Cancelled", value: campaignsStats.cancelled },
                                    ]}
                                    margin={{ top: 20, right: 10, left: 10, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 11 }} dy={10} interval={0} />
                                    <YAxis hide={true} />
                                    <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F5F7FA' }} />
                                    <Bar
                                        dataKey="value"
                                        fill="#475569"
                                        radius={[4, 4, 0, 0]}
                                        maxBarSize={40}
                                    >
                                        <LabelList dataKey="value" position="top" fill="var(--navy)" fontSize={12} fontWeight={600} offset={10} formatter={(v) => v === 0 ? "0" : v} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                </div>

                {/* Primary Data Row */}
                <div className="grid grid-cols-1 gap-6">

                    {/* Inbox/Messages List */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)] flex flex-col min-h-[400px]">
                        <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 rounded-t-xl">
                            <div>
                                <h3 className="text-sm font-bold text-slate-900">Recent Communications</h3>
                                <p className="text-xs text-slate-500 mt-0.5">Unread messages needing attention</p>
                            </div>
                            <span className="bg-blue-50 text-blue-700 border border-blue-100 text-xs font-bold px-2.5 py-1 rounded-full">
                                {unreadMessages.length} Unread
                            </span>
                        </div>
                        <div className="flex-1 overflow-y-auto w-full p-2 h-[320px]">
                            {unreadMessages.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-3">
                                    <MessageSquare className="w-8 h-8 opacity-20" />
                                    <p className="text-sm">Inbox is completely caught up.</p>
                                </div>
                            ) : (
                                <>
                                    {(isCommsExpanded ? unreadMessages : unreadMessages.slice(0, 3)).map((msg) => (
                                        <div
                                            key={msg.id}
                                            onClick={() => navigate("/Chat")}
                                            className="p-4 hover:bg-[#F5F7FA] rounded-lg cursor-pointer transition-colors border-b border-slate-100 last:border-0 flex items-start gap-4 group"
                                        >
                                            <div className="h-10 w-10 shrink-0 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 font-semibold text-sm group-hover:bg-white transition-colors">
                                                {msg.first_name?.[0]?.toUpperCase() || "U"}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-center mb-0.5">
                                                    <p className="text-sm font-semibold text-slate-900 truncate">
                                                        {msg.first_name} {msg.last_name}
                                                    </p>
                                                    <p className="text-xs font-medium text-slate-400 shrink-0 ml-2">
                                                        {new Date(msg.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                                        <span className="mx-1">•</span>
                                                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                    </p>
                                                </div>
                                                <p className="text-sm text-slate-600 line-clamp-1 pr-4">{msg.message}</p>
                                            </div>
                                        </div>
                                    ))}

                                    {unreadMessages.length > 3 && (
                                        <button
                                            onClick={() => setIsCommsExpanded(!isCommsExpanded)}
                                            className="w-full py-3 mt-1 flex items-center justify-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-colors border border-transparent hover:border-slate-100"
                                        >
                                            {isCommsExpanded ? (
                                                <>View less <ChevronUp className="w-4 h-4" /></>
                                            ) : (
                                                <>Show {unreadMessages.length - 3} more <ChevronDown className="w-4 h-4" /></>
                                            )}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;

